import ResponsiveImage from "../components/common/ResponsiveImage";
import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Copy, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

function copy(text) {
  if (navigator.clipboard) navigator.clipboard.writeText(text);
}

export default function Login() {
  const { user, login, completeMfa, completeRecovery } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challengeId, setChallengeId] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const nav = useNavigate();

  if (user && recoveryCodes.length === 0) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
    else if (res.mfaRequired) {
      setChallengeId(res.challengeId);
      setEnrollment(res.enrollmentSecret ? { secret: res.enrollmentSecret, uri: res.enrollmentUri } : null);
    }
    else nav("/", { replace: true });
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = recoveryMode
      ? await completeRecovery(challengeId, recoveryCode.trim())
      : await completeMfa(challengeId, mfaCode.trim());
    setLoading(false);
    if (!res.ok) setError(res.error);
    else {
      setMfaCode("");
      setRecoveryCode("");
      setEnrollment(null);
      if (res.recoveryCodes?.length) setRecoveryCodes(res.recoveryCodes);
      else nav("/", { replace: true });
    }
  };

  const continueToApp = () => {
    setRecoveryCodes([]);
    nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12" data-testid="login-page">
      <form onSubmit={challengeId ? verifyMfa : onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
        <div className="flex flex-col items-center text-center gap-3 mb-2">
          <ResponsiveImage imageWidth={960} src="/voocly-logo.png" alt="Voocly" className="w-44 h-auto object-contain" />
        </div>

        {recoveryCodes.length > 0 ? <div className="space-y-4" data-testid="login-recovery-codes-once">
          <div>
            <h2 className="font-semibold">Save your recovery codes</h2>
            <p className="mt-1 text-sm text-muted-foreground">These codes are shown once. Store them in a secure password manager; each can be used once.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 rounded border border-border p-3">{recoveryCodes.map((value) => <code key={value} className="rounded bg-muted px-2 py-1 text-xs">{value}</code>)}</div>
          <Button type="button" className="w-full" onClick={continueToApp}>I saved my codes</Button>
        </div> : !challengeId ? <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@voocly.com"
              required
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password-input"
            />
          </div>
        </div> : <div className="space-y-3" data-testid="mfa-challenge">
          {enrollment && <div className="space-y-2 rounded border border-border bg-muted/30 p-3">
            <div className="text-sm font-medium">Set up your authenticator</div>
            <p className="text-xs text-muted-foreground">Add this secret or URI to an authenticator app, then enter its current six-digit code below.</p>
            <div className="flex justify-center rounded bg-white p-3"><QRCodeSVG value={enrollment.uri} size={192} level="M" includeMargin /></div>
            <div className="flex gap-2"><code className="min-w-0 flex-1 break-all text-xs">{enrollment.secret}</code><Button type="button" size="sm" variant="outline" onClick={() => copy(enrollment.secret)} aria-label="Copy setup secret"><Copy className="w-3.5 h-3.5" /></Button></div>
            <div className="flex gap-2"><code className="min-w-0 flex-1 break-all text-xs">{enrollment.uri}</code><Button type="button" size="sm" variant="outline" onClick={() => copy(enrollment.uri)} aria-label="Copy authenticator URI"><Copy className="w-3.5 h-3.5" /></Button></div>
          </div>}
          <div className="text-sm text-muted-foreground">Confirm your identity to finish signing in. No session is created until this step succeeds.</div>
          {recoveryMode ? <div className="space-y-1.5">
            <Label htmlFor="recovery-code" className="text-xs">Recovery code</Label>
            <Input id="recovery-code" value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} autoComplete="one-time-code" placeholder="Your unused recovery code" required data-testid="recovery-code-input" />
          </div> : <div className="space-y-1.5">
            <Label htmlFor="mfa-code" className="text-xs">Authenticator code</Label>
            <Input id="mfa-code" inputMode="numeric" pattern="[0-9]*" maxLength={8} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, ""))} autoComplete="one-time-code" placeholder="6-digit code" required data-testid="mfa-code-input" />
          </div>}
          <Button type="button" variant="link" className="px-0 text-xs" onClick={() => { setRecoveryMode((current) => !current); setError(""); }} data-testid="toggle-recovery-btn">{recoveryMode ? "Use authenticator code" : "Use a recovery code"}</Button>
        </div>}

        {error && (
          <div className="text-xs px-3 py-2 rounded-md bg-[hsl(var(--status-danger-bg))] text-[hsl(var(--status-danger-text))] border border-[hsl(var(--status-danger-border))]" data-testid="login-error">
            {error}
          </div>
        )}

        {recoveryCodes.length === 0 && <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : challengeId ? "Verify and sign in" : "Sign in"}
        </Button>}

        {recoveryCodes.length === 0 && <div className="text-[11px] text-muted-foreground text-center border-t border-border pt-4">
          Voocly internal admins only.
        </div>}
      </form>
    </div>
  );
}
